<template>
  <div>
    <v-progress-linear v-if="isGenerating" indeterminate></v-progress-linear>
    <div class="d-flex">
      <div class="d-flex flex-column aggregates-sidebar-nav" style="border-right: 1px solid var(--light-4);">
        <div v-for="nav in navs" :key="nav.name">
          <v-tooltip
            max-width="600"
            location="right"
            :text="`The linked repository (${githubRepo}) is not accessible with your connected GitHub accounts.
             It may have been deleted from GitHub or linked by another team member.
             You can either ask the owner for access, or reset the code view to unlink it.`"
            :disabled="!(nav.name === 'github' && isRepoInaccessible)"
          >
            <template #activator="{ props }">
              <div v-bind="props">
                <v-btn
                  :title="nav.label"
                  :class="[
                    'text-center',
                    activeNav === nav.name ? 'bg-grey-lighten-2 text-primary' : 'text-medium-emphasis'
                  ]"
                  :disabled="isLoading || (nav.name === 'github' && isRepoInaccessible)"
                  variant="elevated"
                  elevation="0"
                  :data-test="nav.name"
                  @click="activeNav = nav.name"
                  style="width: 48px; height: 48px; min-width: 48px; min-height: 48px; padding: 0;"
                >
                  <v-icon>{{ nav.icon }}</v-icon>
                </v-btn>
              </div>
            </template>
          </v-tooltip>
        </div>
      </div>
      <div class="w-100 position-relative" style="height: 820px">
        <workflow-aggregates-template
          v-if="activeNav === 'template'"
          :isGenerating="isGenerating"
          :workflowObject="fileObjects.__workflow"
          :templates="availableTemplates"
          :isSwaggerSynced="isSwaggerSynced"
          :selectedTemplate="selectedTemplate"
          :selectedRepo="selectedRepo"
          :isCommitted="!!boundedContext.githubRepo && Object.keys(diffObjects).length === 0"
          :boundedContext="boundedContext"
          :currentWorkflow="currentWorkflow"
          :getDomainItemFinalPrompt="getDomainItemFinalPrompt"
          :detectDomainChanges="checkDiff"
          :validationErrors="validationErrors"
          :validationWarnings="validationWarnings"
          :validationNotices="validationNotices"
          v-model:fileTree="fileTree"
          v-model:fileObjects="fileObjects"
          v-model:selectedAiModel="selectedAiModel"
          v-model:isAborted="isAborted"
          @updateFramework="(framework) => updateFramework(framework)"
          @updateApi="(includeBE, includeFE, specificDomainIds) => !selectedTemplate?.isLegacy ? updateApi(includeBE, includeFE, specificDomainIds) : updateApiNoTemplate()"
          @abortUpdate="abortUpdate"
          @clearApi="clearApi()"
          @saveFile="saveFile"
          @deleteFile="deleteFile"
          @pushSwaggerToGithub="pushSwaggerToGithub"
          @update:object="handleObjectUpdate"
          @navigateToDomainView="navigateToDomainView"
        />
        <workflow-aggregates-project
          v-if="activeNav === 'code'"
          v-model:fileTree="fileTree"
          v-model:fileObjects="fileObjects"
          :saveFile="saveFile"
          :deleteFile="deleteFile"
          :isLegacy="selectedTemplate?.isLegacy"
          :getDomainItemFinalPrompt="getDomainItemFinalPrompt"
          @update:object="handleObjectUpdate"
        />
        <workflow-aggregates-github
          v-if="activeNav === 'github'"
          v-model:selectedRepo="selectedRepo"
          v-model:selectedBranch="githubBranch"
          v-model:diffObjects="diffObjects"
          :githubRepo="githubRepo"
          :githubAccountLogin="githubAccountLogin"
          :fileObjects="fileObjects"
          :branches="branches"
          :linkGitHubRepo="linkGitHubRepo"
          :refreshGithub="checkSyncWithRepo"
          :unlinkGitHub="unlinkGitHubRepo"
          :makeCommit="makeCommit"
          :saveFile="saveFile"
          @onRepoCreated="onRepoCreated"
        />
        <div v-if="isLoading" class="position-absolute top-0 left-0 w-100 h-100 d-flex" style="background: var(--light-3);">
          <div v-if="typeof loadingText === 'string'" class="d-flex ma-auto justify-center align-center ga-3 flex-column" style="user-select: none;">
            <v-progress-circular
              color="primary"
              size="36"
              indeterminate
            />
            <span v-html="loadingText"></span>
          </div>
          <v-btn
            v-if="typeof loadingText === 'string' && isGenerating"
            @click="abortCodeGeneration"
            :disabled="isAborted"
            class="position-absolute"
            style="bottom: 24px; left: 50%; transform: translateX(-50%);"
          >
            <v-icon start>mdi-stop</v-icon>
            <span>Stop Generating</span>
          </v-btn>
          <v-card v-if="typeof loadingText !== 'string'" loading class="ma-auto">
            <v-card-title style="border-bottom: 1px solid var(--light-4)">
              <v-icon size="20">mdi-code-tags</v-icon>
              <span class="ml-2" style="font-size: 16px">
                {{ isCodeGenReady ? 'Generating Code' : 'Getting Ready ...' }}
              </span>
            </v-card-title>
            <v-card-text class="pt-2">
              <div v-if="!isCodeGenReady">
                <div class="d-flex flex-column ga-3 align-center" style="width: 500px;">
                  <v-skeleton-loader
                    v-for="n in 3"
                    :key="n"
                    type="list-item-two-line"
                    class="w-100"
                  />
                </div>
              </div>
              <div
                v-else
                class="d-flex flex-column ga-3 overflow-y-auto"
                style="width: 500px;max-height: 400px;user-select: none;"
              >
                <div v-for="task in loadingText" :key="task.id" class="d-flex ga-2 pb-1" :class="{ 'task-completed': task.completed }">
                  <v-icon v-if="task.completed" color="success" size="20">mdi-check-circle</v-icon>
                  <v-icon v-else-if="task.failed" color="error" size="20">mdi-close-circle</v-icon>
                  <v-progress-circular
                    v-else
                    color="primary"
                    size="20"
                    width="2"
                    indeterminate
                  />
                  <span style="max-width: 450px" v-html="task.text"></span>
                </div>
              </div>
              <div class="d-flex justify-sm-end mt-2">
                <v-btn @click="abortCodeGeneration" :disabled="isAborted">
                  <v-icon start>mdi-stop</v-icon>
                  <span>Stop Generating</span>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { TEMPLATE_SETTINGS, LEGACY_PROJECT_TEMPLATE } from '@/codegen/constants'
import WorkflowAggregatesTemplate from '@/components/WorkflowAggregatesTemplate.vue'
import WorkflowAggregatesProject from '@/components/WorkflowAggregatesProject.vue'
import WorkflowAggregatesGithub from '@/components/WorkflowAggregatesGithub.vue'
import {
  renderTree,
  getLanguageFromFilename,
  calcGitBlobHash,
  diffArrays
} from '@/codegen/utils'
import { convertWorkflowToSwaggerYaml } from '@/codegen/apiSpecificationBuilder'
import gitHubApi from '@/api/gitHubApi'
import { sanitizeCardDescription } from '@/utils/sanitize'
import _ from 'lodash'
import { streamTextResponse } from '@/ai/api'
import safeStructuredClone from '@/utils/clone'
import { cardHelpers } from '@/mixins/cardHelpers'
import lambdaCodegen from '@/mixins/lambdaCodegen'
import { createFileBatches } from '@/utils/batching'
import { validateDomainModel, Severity } from '@/utils/domainModelValidator'
import {
  AI_USER_PROMPT_ENTITY_NODEJS,
  AI_USER_PROMPT_COMMAND_NODEJS,
  AI_USER_PROMPT_READMODEL_NODEJS,
  AI_USER_PROMPT_GWT_NODEJS,
  AI_PROMPT_NODEJS_EXAMPLE_ENTITY,
  AI_PROMPT_NODEJS_EXAMPLE_COMMAND,
  AI_PROMPT_NODEJS_EXAMPLE_READMODEL,
  AI_PROMPT_NODEJS_EXAMPLE_GWT,
  AI_PROMPT_DATABASE_OPERATIONS,
  generateCodeMarkdown
} from '@/shared/prompts'
import { DomainModelRole } from '@/constants/cardTypes'
import { SchemaType } from '@/constants/schema'
import {
  findEntitiesInWorkflow,
  findCommandsInWorkflow,
  findReadModelsInWorkflow,
  findGWTsInWorkflow
} from '@/shared/codegen/domainExtractors'

export default {
  components: {
    WorkflowAggregatesTemplate,
    WorkflowAggregatesProject,
    WorkflowAggregatesGithub
  },
  mixins: [lambdaCodegen],
  emits: ['navigateToDomainView'],
  props: [
    'boundedContext'
  ],
  data () {
    return {
      isLoading: false,
      loadingText: '',
      activeNav: 'template',
      navs: [
        { name: 'template', icon: 'mdi-domain', label: 'Domain Tab' },
        { name: 'code', icon: 'mdi-code-tags', label: 'Code Tab' },
        { name: 'github', icon: 'mdi-github', label: 'GitHub Tab' }
      ],
      isGenerating: false,
      isInitializing: false,
      fileTree: {},
      fileObjects: {},
      selectedRepo: null,
      githubAccountLogin: this.boundedContext.githubAccountLogin || '',
      githubRepo: this.boundedContext.githubRepo || '',
      githubBranch: this.boundedContext.githubBranch || null,
      repos: [],
      branches: [],
      diffObjects: {},
      selectedAiModel: 4,
      workflowVersion: -1,
      isRepoInaccessible: false,
      abortController: {},
      isAborted: false
    }
  },
  computed: {
    ...mapState({
      workflow: (state) => state.workflow.workflow,
      savedObjects: (state) => state.boundedContexts.boundedContexts,
      githubAccounts: (state) => state.github.githubAccounts,
      failedGenerations: (state) => state.workflow.failedGenerations
    }),
    oldWorkflow () {
      const contents = this.fileObjects.__workflow?.contents
      if (!contents) {
        return contents
      }

      const parsed = JSON.parse(contents)
      const entities = (parsed.schemas || []).filter((schema) => schema.type === SchemaType.ENTITY)

      return { ...parsed, entities }
    },
    oldEntities () {
      return (this.oldWorkflow?.entities || []).map((entity) => ({
        id: entity.id,
        name: entity.name,
        fields: (entity.fields || []).map((field) => ({
          name: field.name,
          exampleData: field.exampleData,
          cardinality: field.cardinality,
          tags: field.tags,
          relatedEntityId: field.relatedEntityId ?? undefined,
          dataType: field.dataType ?? 'string'
        }))
      }))
    },
    oldCommands () {
      return findCommandsInWorkflow(this.oldWorkflow, this.boundedContext?.id)
    },
    oldReadModels () {
      return findReadModelsInWorkflow(this.oldWorkflow, this.boundedContext?.id, this.currentEntities)
    },
    oldGWTs () {
      return findGWTsInWorkflow(this.oldWorkflow, this.boundedContext?.id)
    },
    currentEntities () {
      return findEntitiesInWorkflow(this.workflow, this.boundedContext?.id)
    },
    currentCommands () {
      return findCommandsInWorkflow(this.workflow, this.boundedContext?.id)
    },
    currentReadModels () {
      return findReadModelsInWorkflow(this.workflow, this.boundedContext?.id, this.currentEntities)
    },
    currentGWTs () {
      return findGWTsInWorkflow(this.workflow, this.boundedContext?.id)
    },
    currentWorkflow () {
      return { ...this.workflow || {}, entities: this.currentEntities }
    },
    selectedTemplate () {
      if (this.boundedContext.templateId === LEGACY_PROJECT_TEMPLATE.name) {
        return LEGACY_PROJECT_TEMPLATE
      }

      return TEMPLATE_SETTINGS.find((item) => item.name === this.boundedContext.templateId) || null
    },
    availableTemplates () {
      if (this.selectedTemplate?.isLegacy) {
        return [LEGACY_PROJECT_TEMPLATE, ...TEMPLATE_SETTINGS]
      }

      return TEMPLATE_SETTINGS
    },
    currentTechStack () {
      const techStacks = [
        ' - Language: JavaScript ESM',
        this.selectedTemplate?.name ? ` - Framework: ${this.selectedTemplate.name}` : '',
        this.selectedTemplate?.database ? ` - Database: ${this.selectedTemplate.database}` : '',
        ' - Test Framework: Jest'
      ]
      return techStacks.filter(Boolean).join('\n')
    },
    isSwaggerSynced () {
      if (!this.workflow || !this.boundedContext) {
        return false
      }
      const githubSwaggerObject = this.diffObjects['server/src/docs/swagger.yaml']
      const currentSwagger = convertWorkflowToSwaggerYaml(this.currentWorkflow, this.boundedContext)
      return githubSwaggerObject?.githubContents === currentSwagger
    },
    allFiles () {
      return Object.values(this.fileObjects || {}).filter((file) =>
        file.filepath !== '__workflow' && !file.filepath.includes('.qlerify/'))
    },
    isCodeGenReady () {
      return Array.isArray(this.loadingText) && this.loadingText.length > 0
    },
    validationResults () {
      return validateDomainModel(this.workflow, this.boundedContext)
    },
    validationErrors () {
      return (this.validationResults.issues || []).filter((i) => i.severity === Severity.MAJOR)
    },
    validationWarnings () {
      return (this.validationResults.issues || []).filter((i) => i.severity === Severity.MINOR)
    },
    validationNotices () {
      return (this.validationResults.issues || []).filter((i) => i.severity === Severity.NOTICE)
    }
  },
  watch: {
    githubBranch (_) {
      this.checkSyncWithRepo()
    }
  },
  async mounted () {
    await this.checkAndRestoreGenerationState()

    if (!this.isGenerating) {
      this.isLoading = true
      this.loadingText = 'Loading Github Repositories ...'
    }

    if (this.savedObjects && this.savedObjects[this.boundedContext.id]) {
      if (!this.isGenerating) {
        this.loadingText = 'Loading Files ...'
      }
      this.fileTree = {}
      this.addFiles(this.savedObjects[this.boundedContext.id])
      await this.loadApiSpecification()
      if (this.githubAccountLogin && this.githubRepo) {
        await this.listGitHubRepos(this.githubAccountLogin)
        this.selectedRepo = this.repos.find((_repo) => _repo.full_name === this.githubRepo)
        if (this.selectedRepo) {
          this.isRepoInaccessible = false
          await this.listRepoBranches(this.githubAccountLogin, this.selectedRepo.owner.login, this.selectedRepo.name)
          await this.checkSyncWithRepo()
        } else {
          this.isRepoInaccessible = true
        }
      }
      if (!this.isGenerating) {
        this.isLoading = false
      }
    } else if (this.boundedContext.id) {
      if (!this.isGenerating) {
        this.loadingText = 'Loading Files ...'
      }
      this.$store.dispatch('boundedContexts/getObjectsFromServer', {
        boundedContextId: this.boundedContext.id
      }).then(async (objects) => {
        this.fileTree = {}
        this.addFiles(objects)
        await this.loadApiSpecification()
        if (this.githubAccountLogin && this.githubRepo) {
          await this.listGitHubRepos(this.githubAccountLogin)
          this.selectedRepo = this.repos.find((_repo) => _repo.full_name === this.githubRepo)
          if (this.selectedRepo) {
            this.isRepoInaccessible = false
            await this.listRepoBranches(this.githubAccountLogin, this.selectedRepo.owner.login, this.selectedRepo.name)
            await this.checkSyncWithRepo()
          } else {
            this.isRepoInaccessible = true
          }
        }
        // Only clear loading if not generating code
        if (!this.isGenerating) {
          this.isLoading = false
        }
      })
    }
  },
  methods: {
    checkDiff (type) {
      const map = {
        entity: [this.oldEntities, this.currentEntities],
        command: [this.oldCommands, this.currentCommands],
        readmodel: [this.oldReadModels, this.currentReadModels],
        gwt: [this.oldGWTs, this.currentGWTs]
      }

      const [oldArr, newArr] = map[type] || []
      return oldArr && newArr ? diffArrays(oldArr, newArr) : []
    },
    // Advanced Methods
    async loadApiSpecification () {
      const oldWorkflow = this.fileObjects.__workflow

      if (!oldWorkflow) {
        await this.initOldWorkflow()
      }

      // Clear template if no starter files are found (but not for legacy projects)
      if (!!this.selectedTemplate && !this.selectedTemplate.isLegacy && !this.fileTree.root?.server) {
        await this.updateBoundedContext({ templateId: null }, 'updated template on bounded context')
      }

      // Saving domain root level prompts
      await this.saveDomainRootPrompt()
      const domainModelTypes = ['entity', 'command', 'readmodel', 'gwt']
      await Promise.all(domainModelTypes.map((type) => this.saveDomainItemPrompt(type)))

      // Merge cards from old events into current workflow events
      let oldWorkflowObj
      const oldContents = this.fileObjects.__workflow?.contents

      if (oldContents) {
        try {
          oldWorkflowObj = JSON.parse(oldContents)
        } catch {
          oldWorkflowObj = null
        }
      }

      let newWorkflowObj = _.cloneDeep(oldWorkflowObj || {})

      if (Array.isArray(oldWorkflowObj?.schemas) && Array.isArray(this.workflow?.schemas)) {
        const newIds = new Set(this.workflow.schemas.map((dm) => dm.id))
        newWorkflowObj.schemas = oldWorkflowObj?.schemas?.filter((dm) => newIds.has(dm.id))
      }

      if (Array.isArray(oldWorkflowObj?.domainEvents) && Array.isArray(this.workflow?.domainEvents)) {
        const oldEventMap = new Map(oldWorkflowObj?.domainEvents?.map((e) => [e.id, e]))

        const updatedEvents = this.workflow.domainEvents.map((event) => {
          const oldEvent = oldEventMap.get(event.id)

          const defaultRts = (event.cards || []).filter((rt) => {
            const domainModelRole = rt.cardType?.domainModelRole
            return !domainModelRole || ![
              DomainModelRole.COMMAND,
              DomainModelRole.READ_MODEL,
              DomainModelRole.GIVEN_WHEN_THEN
            ].includes(domainModelRole)
          })

          const mergedCards = [
            ...defaultRts,
            ...(oldEvent?.cards || [])
          ]

          const uniqueCards = Array.from(
            new Map(mergedCards.map((rt) => [rt.id, rt])).values()
          )

          return {
            ...event,
            cards: uniqueCards
          }
        })

        newWorkflowObj = {
          ...newWorkflowObj,
          domainEvents: updatedEvents
        }
      }

      if (!_.isEqual(newWorkflowObj, oldWorkflowObj)) {
        await this.saveFile({
          ...this.fileObjects.__workflow,
          contents: JSON.stringify(newWorkflowObj, null, 2)
        })
      }
    },
    async fetchAndSaveStarterFiles (repoName, repoPath, localPrefix) {
      const repoInfo = {
        login: this.githubAccounts?.length ? this.githubAccounts[0].login : 'Qlerify',
        owner: 'Qlerify',
        repoName,
        branch: 'main'
      }

      const listResponse = await gitHubApi.listGitHubRepoFiles(
        repoInfo.login,
        repoInfo.owner,
        repoInfo.repoName,
        repoPath,
        repoInfo.branch
      )

      if (!listResponse.success) {
        this.$store.commit('snackbar/showMessage', {
          content: listResponse.errorText,
          timeout: 3000,
          color: 'red'
        }, { root: true })
        return false
      }

      const filesToFetch = listResponse.resData.filter((file) => file.type === 'blob')
      if (filesToFetch.length === 0) {
        return true
      }

      const batchingInput = filesToFetch.map((file) => ({ path: file.path, type: 'blob' }))
      const fileBatches = createFileBatches(batchingInput)
      const batchPromises = fileBatches.map((batch) =>
        gitHubApi.fetchGitHubRepoFileContent(
          repoInfo.login,
          repoInfo.owner,
          repoInfo.repoName,
          repoInfo.branch,
          batch
        )
      )

      let allFetchedFiles
      try {
        const allResponses = await Promise.all(batchPromises)
        allFetchedFiles = allResponses.flatMap((response) => (response?.success ? response.resData : []))
      } catch (error) {
        this.$store.commit('snackbar/showMessage', {
          content: 'An error occurred while fetching starter project.',
          timeout: 3000,
          color: 'red'
        }, { root: true })
        return false
      }

      const savePromises = allFetchedFiles.map((file) => {
        const newFileObject = {
          filepath: file.path.replace(repoPath, localPrefix),
          boundedContextId: this.boundedContext.id,
          contents: file.content,
          tags: []
        }
        return this.saveFile(newFileObject)
      })

      await Promise.all(savePromises)
      return true
    },
    async saveDomainRootPrompt () {
      const domains = ['command', 'readmodel', 'entity', 'gwt']
      const isLegacy = this.selectedTemplate?.isLegacy

      const legacyPrompts = {
        entity: generateCodeMarkdown,
        command: generateCodeMarkdown,
        readmodel: generateCodeMarkdown,
        gwt: generateCodeMarkdown
      }

      const regularPrompts = {
        entity: AI_USER_PROMPT_ENTITY_NODEJS,
        command: AI_USER_PROMPT_COMMAND_NODEJS,
        readmodel: AI_USER_PROMPT_READMODEL_NODEJS,
        gwt: AI_USER_PROMPT_GWT_NODEJS
      }

      const promptTemplates = isLegacy ? legacyPrompts : regularPrompts
      await Promise.all(domains.map(async (domain) => {
        const domainRootPath = `root/.qlerify/${domain}-template.md`
        let domainObj = this.fileObjects[domainRootPath]
        if (!domainObj) {
          domainObj = {
            id: null,
            filepath: `root/.qlerify/${domain}-template.md`,
            boundedContextId: this.boundedContext.id,
            contents: promptTemplates[domain],
            draftContents: null,
            tags: [],
            isDirty: false
          }
          this.addFile(domainObj, true)
          this.fileObjects[domainRootPath] = domainObj
          await this.saveFile(this.fileObjects[domainRootPath])
        }
      }))
    },
    async saveDomainItemPrompt (type) {
      const isLegacy = this.selectedTemplate?.isLegacy

      const legacyPrompts = {
        entity: generateCodeMarkdown,
        command: generateCodeMarkdown,
        readmodel: generateCodeMarkdown,
        gwt: generateCodeMarkdown
      }

      const regularPrompts = {
        entity: AI_USER_PROMPT_ENTITY_NODEJS,
        command: AI_USER_PROMPT_COMMAND_NODEJS,
        readmodel: AI_USER_PROMPT_READMODEL_NODEJS,
        gwt: AI_USER_PROMPT_GWT_NODEJS
      }

      const promptTemplates = isLegacy ? legacyPrompts : regularPrompts
      const currentItems = {
        entity: this.currentEntities,
        command: this.currentCommands,
        readmodel: this.currentReadModels,
        gwt: this.currentGWTs
      }[type]

      const normalizeName = (str) =>
        (str || '')
          .toLowerCase()
          .replace(/\s+/g, '-')

      const getTemplatePath = (name) =>
        `root/.qlerify/${type}-templates/${type}-${normalizeName(name)}.md`

      // --- DELETE UNUSED TEMPLATE FILES ---
      const allKeys = Object.keys(this.fileObjects)
      const existingTemplateKeys = allKeys.filter((key) =>
        key.startsWith(`root/.qlerify/${type}-templates/${type}-`)
      )

      const activeTemplateKeys = currentItems.map((item) =>
        getTemplatePath(item.name || item.description)
      )

      await Promise.all(
        existingTemplateKeys
          .filter((key) => !activeTemplateKeys.includes(key))
          .map((key) => this.deleteFile(this.fileObjects[key]))
      )

      // --- ADD MISSING TEMPLATE FILES ---
      await Promise.all(
        currentItems.map(async (item) => {
          const filePath = getTemplatePath(item.name || item.description)

          if (!this.fileObjects[filePath]) {
            const newFile = {
              id: null,
              filepath: filePath,
              boundedContextId: this.boundedContext.id,
              contents: promptTemplates[type],
              draftContents: null,
              tags: [],
              isDirty: false
            }

            this.addFile(newFile, true)
            this.fileObjects[filePath] = newFile
            await this.saveFile(newFile)
          }
        })
      )
    },
    extractMinimalMetadata (type, diff, associatedEntity) {
      const target = diff.new || diff.old

      const metadata = { id: target.id }

      if (type === 'entity') {
        metadata.name = target.name
      } else if (type === 'command') {
        metadata.description = target.description
        metadata.gwtDescriptions = target.gwtDescriptions || []
        metadata.associatedEntityName = associatedEntity?.name || null
      } else if (type === 'readmodel') {
        metadata.allIds = target.allIds || [target.id]
        metadata.description = target.description
        metadata.allDescriptions = target.allDescriptions || [target.description]
        metadata.associatedEntityName = associatedEntity?.name || null
      } else if (type === 'gwt') {
        metadata.description = target.description
        const event = this.workflow.domainEvents.find((e) =>
          e.cards?.some((card) => card.id === target.id)
        )
        if (event) {
          const commandCard = event.cards.find((card) => card.cardType?.domainModelRole === DomainModelRole.COMMAND)
          metadata.eventDescription = commandCard?.description || 'Event'
        }
      }

      return metadata
    },
    getDomainItemFinalPrompt (fileObject, diffData) {
      if (!fileObject && !diffData) {
        return
      }

      let targetModel
      let type
      if (fileObject) {
        const filename = fileObject.filepath.split('/').pop().replace('.md', '')
        const parts = filename.split('-')
        type = parts.shift()
        const normalizedDescription = parts.join('-')

        const domainModels = {
          entity: this.currentEntities,
          command: this.currentCommands,
          readmodel: this.currentReadModels,
          gwt: this.currentGWTs
        }
        targetModel = domainModels[type].find((item) => {
          const currentNormalized = (item.name || item.description).toLowerCase().replace(/\s+/g, '-')
          return currentNormalized === normalizedDescription
        })
      } else {
        targetModel = diffData.diff.new || diffData.diff.old
        type = diffData.type
      }

      const diffs = diffData ? [] : this.checkDiff(type)
      let diff = diffData?.diff || diffs.find((d) => (d.new || d.old).id === targetModel.id)
      if (!diff) {
        diff = { type: 'update', old: targetModel, new: targetModel }
      }

      let associatedEntity = null
      let readModelAssociatedEntity = null
      if (type === 'entity') {
        associatedEntity = targetModel
      } else {
        const eventForCard = this.workflow.domainEvents.find((event) =>
          event.cards?.some((card) => card.id === targetModel.id)
        )

        if (eventForCard) {
          const entityCard = eventForCard.cards?.find((card) => card.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT)
          if (entityCard) {
            associatedEntity = this.currentEntities.find((entity) => entity.id === entityCard.schemaId)
          }
        }
      }

      if (type === 'readmodel') {
        readModelAssociatedEntity = this.currentEntities.find((e) => e.id === targetModel.entityId)
      }

      const relatedEntities = []
      for (const field of diff.new?.fields ?? []) {
        if (field.relatedEntityId) {
          const entity = this.currentEntities.find((e) => e.id === field.relatedEntityId)

          if (entity && !relatedEntities.some((e) => e.id === entity.id)) {
            relatedEntities.push(entity)
          }
        }
      }

      if (readModelAssociatedEntity) {
        relatedEntities.push(readModelAssociatedEntity)
      }

      if (!associatedEntity) {
        return
      }

      const entities = this.workflow.schemas ?? []
      const events = this.workflow.domainEvents ?? []
      const relatedEntityIdsSet = new Set(relatedEntities.map((e) => e.id))

      let swaggerDocs = ''
      if (type === 'entity') {
        const filteredEntities = entities.reduce((result, item) => {
          if (item.type === SchemaType.ENTITY && (item.id === associatedEntity.id || relatedEntityIdsSet.has(item.id))) {
            result.push({
              ...item,
              boundedContext: this.boundedContext.id
            })
          }

          return result
        }, [])
        const workflowData = {
          version: this.workflow.version,
          schemas: filteredEntities,
          entities: filteredEntities,
          domainEvents: []
        }

        swaggerDocs = convertWorkflowToSwaggerYaml(workflowData, this.boundedContext)
      } else if (type === 'command') {
        const command = diff.new || diff.old
        const event = events.find((event) => event.cards?.some((card) => card.id === command.id))
        const relevantCards = event.cards?.filter((card) =>
          card.id === command.id || card.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT
        ) || []
        const commandCard = event.cards?.find((card) => card.id === command.id)
        const commandSchema = commandCard?.schemaId
          ? (this.workflow.schemas || []).find((s) => s.id === commandCard.schemaId)
          : null

        const entitySchemas = entities.filter((item) => item.type === SchemaType.ENTITY && (item.id === associatedEntity.id || relatedEntityIdsSet.has(item.id)))
        const workflowData = {
          version: this.workflow.version,
          schemas: commandSchema ? [...entitySchemas, commandSchema] : entitySchemas,
          entities: entitySchemas,
          domainEvents: [
            {
              ...event,
              cards: relevantCards
            }
          ]
        }
        swaggerDocs = convertWorkflowToSwaggerYaml(workflowData, this.boundedContext)
      } else if (type === 'readmodel') {
        const readModel = diff.new || diff.old
        const matchingEvents = events.filter((event) => event.cards?.some((card) => readModel.allIds.includes(card.id)))

        const querySchemas = matchingEvents.flatMap((event) =>
          event.cards
            ?.filter((card) => readModel.allIds.includes(card.id) && card.schemaId)
            .map((card) => (this.workflow.schemas || []).find((s) => s.id === card.schemaId))
            .filter(Boolean) || []
        )
        const entitySchemas = entities.filter((item) => item.type === SchemaType.ENTITY && (item.id === associatedEntity.id || relatedEntityIdsSet.has(item.id)))

        const workflowData = {
          version: this.workflow.version,
          schemas: [...entitySchemas, ...querySchemas],
          entities: entitySchemas,
          domainEvents: matchingEvents.map((event) => {
            const relevantCards = event.cards?.filter((card) =>
              readModel.allIds.includes(card.id) || card.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT
            ) || []

            return {
              ...event,
              cards: relevantCards
            }
          })
        }

        swaggerDocs = convertWorkflowToSwaggerYaml(workflowData, this.boundedContext)
      } else if (type === 'gwt') {
        swaggerDocs = convertWorkflowToSwaggerYaml(this.currentWorkflow, this.boundedContext)
      }

      const normalizedDescription = (targetModel.name || targetModel.description)
        .toLowerCase()
        .replace(/\s+/g, '-')
      const domainItemPath = `root/.qlerify/${type}-templates/${type}-${normalizedDescription}.md`
      const promptTemplate = this.fileObjects[domainItemPath].contents

      const EXAMPLE_CODE = {
        entity: AI_PROMPT_NODEJS_EXAMPLE_ENTITY,
        command: AI_PROMPT_NODEJS_EXAMPLE_COMMAND,
        readmodel: AI_PROMPT_NODEJS_EXAMPLE_READMODEL,
        gwt: AI_PROMPT_NODEJS_EXAMPLE_GWT
      }

      const oldFiles = diff.old
        ? Object.values(this.fileObjects)
          .filter((file) => Array.isArray(file.tags) && file.tags.includes(`${type}-${diff.old.id}`))
        : []

      const backendFileTree = this.fileTree.root?.server ? { server: this.fileTree.root.server } : null

      const oldMetadata = diff.old
        ? this.extractMinimalMetadata(type, { old: diff.old, new: diff.old }, associatedEntity)
        : null
      const newMetadata = this.extractMinimalMetadata(type, diff, associatedEntity)

      return promptTemplate
        .replace('{{ TECH_STACKS }}', this.currentTechStack)
        .replace('{{ FOLDER_STRUCTURE }}', '```bash\n' + (backendFileTree ? renderTree(backendFileTree) : '') + '```')
        .replace('{{ DATABASE_OPERATIONS }}', AI_PROMPT_DATABASE_OPERATIONS)
        .replace('{{ EXAMPLE_CODE }}', EXAMPLE_CODE[type])
        .replace('{{ ORIGINAL_FILES }}', JSON.stringify(oldFiles))
        .replace('{{ LEGACY_INFO }}', oldMetadata ? JSON.stringify(oldMetadata, null, 2) : 'N/A - This is a new item')
        .replace('{{ LATEST_INFO }}', JSON.stringify(newMetadata, null, 2))
        .replace('{{ SWAGGER_DOCUMENT }}', swaggerDocs)
        .replace('{{ CURRENT_DATE }}', new Date().toISOString())
    },
    async updateFramework (template) {
      if (this.allFiles.length) {
        await this.clearApi()
      }
      this.isLoading = true
      this.loadingText = 'Saving changes ...'

      await this.updateBoundedContext({ templateId: template.name }, 'updated template on bounded context')

      let frontendSuccess = false

      // command/entity/gwt/readmodel-template.md, __workflow
      this.loadingText = 'Building backend architecture...'
      const backendSuccess = await this.fetchAndSaveStarterFiles('qlerify-starter-javascript-express', '', 'root/server/')

      if (backendSuccess) {
        this.loadingText = 'Building frontend architecture...'
        frontendSuccess = await this.fetchAndSaveStarterFiles('qlerify-starter-typescript-react', '', 'root/client/')
      }

      // If failed to fetch starter project, unset the template again
      if (!frontendSuccess) {
        await this.updateBoundedContext({ templateId: null }, 'updated template on bounded context')
      }

      this.isLoading = false
    },
    async clearApi () {
      this.isLoading = true
      this.loadingText = 'Resetting API ...'

      // Clear repository candidates
      this.repos = []

      await this.$store.dispatch('boundedContexts/deleteBoundedContext', this.boundedContext.id)

      const updatedValues = {
        templateId: null,
        githubAccountLogin: null,
        githubRepo: null,
        githubBranch: null
      }

      await this.updateBoundedContext(updatedValues, 'removed linked github account and repo from bounded context')

      this.githubAccountLogin = ''
      this.githubRepo = ''
      this.githubBranch = ''
      this.selectedRepo = null
      this.isRepoInaccessible = false

      this.fileTree = {}
      this.fileObjects = {}

      await this.loadApiSpecification()
      this.isLoading = false
    },
    handleObjectUpdate (object) {
      const path = object.filepath
      this.fileObjects[path] = object
    },
    async initOldWorkflow () {
      const emptyWorkflow = {
        schemas: [],
        domainEvents: this.workflow.domainEvents.map((event) => ({
          ...event,
          cards: event.cards?.filter((rt) => {
            if (!rt.cardType) {
              return false
            }
            const domainModelRole = rt.cardType.domainModelRole

            return !domainModelRole || ![
              DomainModelRole.COMMAND,
              DomainModelRole.READ_MODEL,
              DomainModelRole.GIVEN_WHEN_THEN
            ].includes(domainModelRole)
          })
        }))
      }
      await this.saveFile({
        id: null,
        filepath: '__workflow',
        boundedContextId: this.boundedContext.id,
        contents: JSON.stringify(emptyWorkflow),
        tags: [],
        isDirty: false
      })
    },
    async updateOldWorkflow (domainId) {
      const oldWorkflow = JSON.parse(this.fileObjects.__workflow.contents)

      // Update or remove entity in schemas
      const currentEntity = this.workflow.schemas.find((m) => m.id === domainId)
      if (currentEntity) {
        const index = oldWorkflow.schemas.findIndex((m) => m.id === domainId)
        if (index !== -1) {
          oldWorkflow.schemas[index] = currentEntity
        } else {
          oldWorkflow.schemas.push(currentEntity)
        }
      } else {
        oldWorkflow.schemas = oldWorkflow.schemas.filter((m) => m.id !== domainId)
      }

      // Checking commands, readmodels and gwts
      for (const event of this.workflow.domainEvents) {
        const currentReq = event.cards?.find((r) => r.id === domainId)
        if (currentReq) {
          let updatedReq = _.cloneDeep(currentReq)
          if (currentReq.cardType?.domainModelRole === DomainModelRole.COMMAND) {
            const gwtCards = cardHelpers.methods.getGwtCards(event)
            const gwtDescriptions = gwtCards.map((card) => sanitizeCardDescription(card.description))
            updatedReq = { ...currentReq, gwtDescriptions }
          }
          // find matching event in oldWorkflow
          const oldEvent = oldWorkflow.domainEvents.find((e) => e.id === event.id)
          if (oldEvent) {
            const idx = oldEvent.cards?.findIndex((r) => r.id === domainId)
            if (idx !== -1) {
              oldEvent.cards[idx] = updatedReq
            } else {
              oldEvent.cards?.push(updatedReq)
            }

            const currentSchema = currentReq.schemaId
              ? this.workflow.schemas?.find((s) => s.id === currentReq.schemaId)
              : null
            if (currentSchema) {
              if (!oldWorkflow.schemas) {
                oldWorkflow.schemas = []
              }
              const schemaIdx = oldWorkflow.schemas.findIndex((s) => s.id === currentSchema.id)
              if (schemaIdx !== -1) {
                oldWorkflow.schemas[schemaIdx] = _.cloneDeep(currentSchema)
              } else {
                oldWorkflow.schemas.push(_.cloneDeep(currentSchema))
              }
            }
          } else {
            // event not in oldWorkflow, add whole event with card
            const currentSchema = currentReq.schemaId
              ? this.workflow.schemas?.find((s) => s.id === currentReq.schemaId)
              : null
            oldWorkflow.domainEvents.push({
              ...event,
              cards: [updatedReq]
            })

            if (currentSchema) {
              if (!oldWorkflow.schemas) {
                oldWorkflow.schemas = []
              }
              if (!oldWorkflow.schemas.some((s) => s.id === currentSchema.id)) {
                oldWorkflow.schemas.push(_.cloneDeep(currentSchema))
              }
            }
          }
        }
      }

      await this.saveFile({
        id: this.fileObjects.__workflow.id,
        filepath: '__workflow',
        boundedContextId: this.boundedContext.id,
        contents: JSON.stringify(oldWorkflow),
        tags: []
      })

      // Update swagger.yaml based on updated oldWorkflow
      const swaggerContent = convertWorkflowToSwaggerYaml(this.oldWorkflow, this.boundedContext)
      const swaggerObject = {
        filepath: 'root/server/src/docs/swagger.yaml',
        boundedContextId: this.boundedContext.id,
        contents: swaggerContent,
        tags: []
      }
      await this.saveFile(swaggerObject)
    },
    abortUpdate () {
      this.isAborted = true
      Object.values(this.abortController).forEach((controller) => {
        if (controller && typeof controller.abort === 'function') {
          controller.abort()
        }
      })
      this.abortController = {}
      this.$store.commit('snackbar/showMessage', { content: 'Code generation has been stopped.', timeout: 3000, color: 'blue' }, { root: true })
      this.$store.dispatch('workflow/closeRetryDialog')
    },
    addFile (object, openDirectory = false) {
      const parts = object.filepath.split('/')

      if (object.filepath !== '__workflow') {
        let curr = this.fileTree
        let currPath = ''
        parts.forEach((part) => {
          if (!curr[part]) {
            curr[part] = {}
          }
          curr = curr[part]

          // make sure new directories are opened
          if (openDirectory && !part.includes('.')) {
            if (currPath) {
              currPath += '/'
            }
            currPath += part
          }
        })
      }

      const objectCopy = { ...object }
      if (!objectCopy.contents) {
        objectCopy.contents = ''
      }
      this.fileObjects[object.filepath] = objectCopy
    },
    addFiles (objects) {
      objects.forEach((object) => {
        this.addFile(object, true)
      })
    },
    updateFileAndCapture (_filepath, pathPrefix, rawContent) {
      let processedContent = rawContent
      const tagMatch = processedContent.match(/=== TAG: (.+?) ===/)
      let tags = []

      if (tagMatch) {
        tags = tagMatch[1].split(',').map((tag) => tag.trim())
        processedContent = processedContent.replace(/=== TAG: .+? ===\s*/, '')
      }

      const codeBlockRegex = /```[\w]*\n?([\s\S]*?)```/
      const codeMatch = processedContent.match(codeBlockRegex)

      const extractedContent = codeMatch ? codeMatch[1] : processedContent
      const trimmedContent = extractedContent.trimEnd()
      const finalContent = trimmedContent + '\n'

      const filepath = _filepath.startsWith(pathPrefix) ? _filepath : pathPrefix + _filepath

      return { filepath, content: finalContent, tags }
    },
    async updateCodeNoTemplate (prompt, pathPrefix = '') {
      const modifiedFilesMap = new Map()

      let buffer = ''
      let currentFilepath = null
      let currentIsDeleted = false
      const fileHeaderRegex = /=== FILE: (.+?)( \(deleted\))? ===\s*/g

      try {
        await streamTextResponse({
          messages: [{ role: 'user', content: prompt }],
          useCase: 'code-generation',
          aiVersion: this.selectedAiModel,
          signal: this.abortController?.signal,
          onChunkReceived: async (chunkResponse) => {
            buffer += chunkResponse
            let match
            while ((match = fileHeaderRegex.exec(buffer)) !== null) {
              const newFilepath = match[1].trim()
              const newIsDeleted = !!match[2]
              const splitIndex = match.index
              const previousContent = buffer.slice(0, splitIndex)

              if (currentFilepath) {
                if (currentIsDeleted) {
                  const deletedObject = this.fileObjects[currentFilepath]
                  deletedObject && await this.deleteFile(deletedObject)
                } else {
                  const { filepath, content, tags } = this.updateFileAndCapture(currentFilepath, pathPrefix, previousContent)
                  modifiedFilesMap.set(filepath, { content, tags })
                }
              }
              currentFilepath = newFilepath
              currentIsDeleted = newIsDeleted
              buffer = buffer.slice(fileHeaderRegex.lastIndex)
              fileHeaderRegex.lastIndex = 0
            }
          }
        })

        if (currentFilepath && buffer.trim()) {
          const { filepath, content, tags } = this.updateFileAndCapture(currentFilepath, pathPrefix, buffer)
          modifiedFilesMap.set(filepath, { content, tags })
        }

        return modifiedFilesMap
      } catch (e) {
        return {}
      }
    },
    async handleUpdateEachDomainNoTemplate (type, diff, baseFileObjectsMap) {
      const resultingStateMap = new Map(safeStructuredClone(Array.from(baseFileObjectsMap)))

      const target = diff.new || diff.old
      const normalizedName = (target.name || target.description || '').toLowerCase().replace(/\s+/g, '-')
      const templatePath = `root/.qlerify/${type}-templates/${type}-${normalizedName}.md`
      const promptTemplate = this.fileObjects[templatePath]?.contents || generateCodeMarkdown

      const relevantFiles = []
      if (diff.old) {
        for (const [filepath, fileObject] of baseFileObjectsMap.entries()) {
          if (Array.isArray(fileObject.tags) && fileObject.tags.some((tag) => tag === `${type}-${diff.old.id}`)) {
            relevantFiles.push({ ...fileObject, filepath })
          }
        }
      }

      const filesString = relevantFiles.length > 0
        ? relevantFiles.map((file) => {
          let text = '=== File: ' + file.filepath + ' ===\n'
          text += '=== TAG: ' + (Array.isArray(file.tags) ? file.tags.join(', ') : '') + ' ===\n'
          text += '```' + getLanguageFromFilename(file.filepath) + '\n'
          text += file.contents
          text += '\n```\n'
          return text
        }).join('')
        : 'NO FILES'

      const gwtDescriptions = diff.new?.gwtDescriptions || []

      const updatePrompt = promptTemplate
        .replaceAll('{{ PROMPT_TYPE }}', type)
        .replace('{{ FOLDER_STRUCTURE }}', '```bash\n' + (this.fileTree ? renderTree(this.fileTree) : '') + '```')
        .replace('{{ ORIGINAL_FILES }}', filesString)
        .replace('{{ LEGACY_INFO }}', JSON.stringify(diff.old, null, 2))
        .replace('{{ LATEST_INFO }}', JSON.stringify(diff.new, null, 2))
        .replace('{{ GWT_DESCRIPTIONS }}', JSON.stringify(gwtDescriptions, null, 2))

      const modifiedFilesMap = await this.updateCodeNoTemplate(updatePrompt, 'root/')

      for (const [path, modifiedFile] of modifiedFilesMap.entries()) {
        if (resultingStateMap.has(path)) {
          const existingFile = resultingStateMap.get(path)
          existingFile.contents = modifiedFile.content
          existingFile.tags = modifiedFile.tags
        } else {
          resultingStateMap.set(path, {
            id: null,
            filepath: path,
            boundedContextId: this.boundedContext.id,
            contents: modifiedFile.content,
            draftContents: null,
            tags: modifiedFile.tags || [],
            isDirty: false
          })
        }
      }

      return resultingStateMap
    },
    async updateApiNoTemplate () {
      this.isGenerating = true
      this.isLoading = true

      let codeStateMap = new Map(Object.entries(this.fileObjects))

      const domainModelTypes = ['entity', 'command', 'readmodel']
      const toVerb = (s) => s[0].toUpperCase() + s.slice(1, -1) + 'ing'

      for (const type of domainModelTypes) {
        const diffs = this.checkDiff(type)
        if (diffs.length > 0) {
          this.loadingText = `Processing ${type} changes...`
          for (const diff of diffs) {
            const target = diff.new || diff.old
            this.loadingText = `${toVerb(diff.type)} ${type}: <b>${target.name || target.description}</b>`

            codeStateMap = await this.handleUpdateEachDomainNoTemplate(type, diff, codeStateMap)
            await this.updateOldWorkflow(target.id)
          }
        }
      }

      const finalCodeStateMap = codeStateMap

      this.loadingText = 'Saving Files...'

      for (const [path, modifiedFile] of finalCodeStateMap.entries()) {
        if (path === '__workflow') {
          continue
        }

        const originalFile = this.fileObjects[path]

        if (originalFile) {
          const contentChanged = originalFile.contents !== modifiedFile.contents
          const tagsChanged = JSON.stringify(originalFile.tags || []) !== JSON.stringify(modifiedFile.tags || [])

          if (contentChanged || tagsChanged) {
            originalFile.draftContents = modifiedFile.contents
            originalFile.tags = modifiedFile.tags || []
            originalFile.isDirty = true
            await this.saveFile(originalFile)
          }
        } else {
          const newFileObject = {
            id: null,
            filepath: path,
            boundedContextId: this.boundedContext.id,
            contents: '',
            draftContents: modifiedFile.contents,
            tags: modifiedFile.tags || [],
            isDirty: true
          }
          this.addFile(newFileObject, true)
          this.fileObjects[path] = newFileObject
          await this.saveFile(this.fileObjects[path])
        }
      }

      this.isLoading = false
      this.isGenerating = false
    },
    async updateBoundedContext (updatedValues, updateNote) {
      const newBoundedContexts = this.workflow.boundedContexts?.map((item) => {
        if (item.id !== this.boundedContext.id) {
          return item
        }

        return { ...item, ...updatedValues }
      })

      return await this.$store.dispatch('workflow/updateWorkflow', {
        projectId: this.workflow.projectId,
        input: {
          id: this.workflow.id,
          expectedVersion: this.workflow.version,
          boundedContexts: newBoundedContexts,
          updateNote: {
            action: updateNote,
            target: ''
          }
        }
      })
    },
    async saveFile (object) {
      const parts = object.filepath.split('/')

      if (!object.id) {
        this.addFile(object, true)
        this.fileObjects[object.filepath] = await this.$store.dispatch('boundedContexts/createObject', object)
      } else {
        const files = this.savedObjects[this.boundedContext.id]
        const originalFile = files ? files.find((f) => f.id === object.id) : null
        const initialContent = originalFile?.contents || ''

        this.fileObjects[object.filepath] = object
        await this.$store.dispatch('boundedContexts/updateObjectContents', object)

        const rootTemplateRegex = /^root\/\.qlerify\/(command|entity|readmodel|gwt)-template\.md$/
        const match = object.filepath.match(rootTemplateRegex)

        if (match) {
          const type = match[1]
          const newItemTemplateContent = object.contents
          const itemTemplatePrefix = `root/.qlerify/${type}-`

          const updatePromises = Object.values(this.fileObjects)
            .filter((file) => {
              const isItemTemplate = file.filepath.startsWith(itemTemplatePrefix) && file.filepath !== object.filepath
              const isUnmodified = file.contents === initialContent

              return isItemTemplate && isUnmodified
            })
            .map((fileToUpdate) => {
              const updatedFile = { ...fileToUpdate, contents: newItemTemplateContent }
              this.fileObjects[fileToUpdate.filepath] = updatedFile

              return this.$store.dispatch('boundedContexts/updateObjectContents', updatedFile)
            })

          if (updatePromises.length > 0) {
            await Promise.all(updatePromises)
          }
        }

        if (object.filepath === '__workflow') {
          this.$store.commit('snackbar/showMessage', { content: 'Saved workflow information', timeout: 3000, color: 'blue' }, { root: true })
        } else {
          this.$store.commit('snackbar/showMessage', { content: 'Saved ' + parts[parts.length - 1], timeout: 3000, color: 'blue' }, { root: true })
        }
      }
      object.isDirty = false
    },
    async deleteFile (object) {
      if (object.id) {
        await this.$store.dispatch('boundedContexts/deleteObject', object)
        const path = object.realpath || object.filepath

        const keys = path.split('/')
        const lastKey = keys.pop()
        let parent = this.fileTree
        for (const key of keys) {
          if (parent[key] === undefined) {
            return
          }
          parent = parent[key]
        }
        delete parent[lastKey]
        delete this.fileObjects[path]
        this.$store.commit('snackbar/showMessage', { content: 'Deleted ' + lastKey, timeout: 3000, color: 'blue' }, { root: true })
      }
    },
    onRepoCreated (login, newRepo) {
      this.repos = [...this.repos, newRepo]
      this.linkGitHubRepo(login, newRepo, 'main')
    },
    // GitHub-Related Functions
    async listGitHubRepos (login, nextPageUrl = null) {
      const response = await gitHubApi.listGitHubRepos(login, nextPageUrl)

      if (response.success) {
        const currentRepos = response.resData?.repos || []
        const pageUrl = response.resData?.nextPageUrl

        this.repos = nextPageUrl ? [...this.repos, ...currentRepos] : currentRepos

        if (pageUrl) {
          await this.listGitHubRepos(login, pageUrl)
        }
      } else {
        if (!response.errorText?.includes('token not found for login')) {
          this.$store.commit('snackbar/showMessage', {
            content: response.errorText,
            timeout: 3000,
            color: 'red'
          }, { root: true })
        }
      }
    },
    async listRepoBranches (login, owner, repo, nextPageUrl) {
      const response = await gitHubApi.listRepoBranches(login, owner, repo, nextPageUrl)

      if (response.success) {
        const currentBranches = response.resData?.branches || []
        const pageUrl = response.resData?.nextPageUrl

        this.branches = nextPageUrl ? [...this.branches, ...currentBranches] : currentBranches

        if (pageUrl) {
          await this.listRepoBranches(login, owner, repo, pageUrl)
        }
      } else {
        this.$store.commit('snackbar/showMessage', {
          content: response.errorText,
          timeout: 3000,
          color: 'red'
        }, { root: true })
      }
    },
    async linkGitHubRepo (login, repo, branch) {
      this.isLoading = true
      this.loadingText = 'Linking with Github repositories ...'

      this.githubAccountLogin = login
      this.githubRepo = repo.full_name

      const updatedValues = {
        githubAccountLogin: login,
        githubRepo: repo.full_name
      }
      await this.updateBoundedContext(updatedValues, 'linked repository')
      this.selectedRepo = repo

      await this.listRepoBranches(login, repo.owner.login, repo.name)
      this.githubBranch = branch
      await this.updateBoundedContext({
        githubBranch: this.githubBranch
      }, 'linked branch')

      this.isLoading = false
      await this.checkSyncWithRepo()
    },
    async checkSyncWithRepo () {
      if (!this.selectedRepo || !Object.keys(this.selectedRepo).length || !this.githubAccountLogin) {
        return
      }

      this.isLoading = true
      this.loadingText = 'Comparing your code with GitHub...'

      const listResponse = await gitHubApi.listGitHubRepoFiles(
        this.githubAccountLogin,
        this.selectedRepo.owner.login,
        this.selectedRepo.name,
        null,
        this.githubBranch
      )

      if (!listResponse.success) {
        this.$store.commit('snackbar/showMessage', {
          content: listResponse.errorText,
          timeout: 3000,
          color: 'red'
        }, { root: true })
        this.isLoading = false
        return
      }

      const githubMap = new Map(
        listResponse.resData
          .filter((file) => file.type === 'blob')
          .map((file) => [file.path, file])
      )

      const currentMap = new Map(
        Object.entries(this.fileObjects)
          .filter(([path]) => path !== '__workflow')
          .map(([path, file]) => [path.replace('root/', ''), file.contents])
      )

      const filesToFetchContentFor = []
      const allPaths = new Set([...currentMap.keys(), ...githubMap.keys()])
      const diffsToProcess = []

      for (const path of allPaths) {
        const localContent = currentMap.get(path)
        const remoteFile = githubMap.get(path)
        const hasLocal = currentMap.has(path)
        const hasRemote = githubMap.has(path)

        if (hasLocal && !hasRemote) {
          diffsToProcess.push({ path, type: 'added', newContent: localContent })
        } else if (!hasLocal && hasRemote) {
          diffsToProcess.push({ path, type: 'deleted' })
          filesToFetchContentFor.push(remoteFile)
        } else if (hasLocal && hasRemote) {
          const normalizedLocalContent = (localContent || '').replace(/\r\n/g, '\n')
          const localByteLength = new TextEncoder().encode(normalizedLocalContent).length

          if (localByteLength !== remoteFile.size || calcGitBlobHash(normalizedLocalContent) !== remoteFile.sha) {
            diffsToProcess.push({ path, type: 'modified', newContent: localContent })
            filesToFetchContentFor.push(remoteFile)
          }
        }
      }

      let fetchedContentMap = new Map()
      if (filesToFetchContentFor.length > 0) {
        const repoInfo = {
          login: this.githubAccountLogin,
          owner: this.selectedRepo.owner.login,
          repoName: this.selectedRepo.name,
          branch: this.githubBranch
        }

        const batchingInput = filesToFetchContentFor.map((file) => ({ path: file.path, type: 'blob' }))
        const fileBatches = createFileBatches(batchingInput)
        const batchPromises = fileBatches.map((batch) =>
          gitHubApi.fetchGitHubRepoFileContent(
            repoInfo.login,
            repoInfo.owner,
            repoInfo.repoName,
            repoInfo.branch,
            batch
          )
        )

        try {
          const allResponses = await Promise.all(batchPromises)
          const allFetchedFiles = allResponses.flatMap((response) => (response?.success ? response.resData : []))
          fetchedContentMap = new Map(allFetchedFiles.map((file) => [file.path, file.content]))
        } catch (error) {
          this.$store.commit('snackbar/showMessage', {
            content: 'Error fetching file contents from GitHub.',
            timeout: 3000,
            color: 'red'
          }, { root: true })
          this.isLoading = false
          return
        }
      }

      const finalDiffs = {}
      for (const diff of diffsToProcess) {
        const oldContent = fetchedContentMap.get(diff.path) || ''
        finalDiffs[diff.path] = {
          filepath: diff.path,
          githubContents: diff.type === 'added' ? null : oldContent,
          originalContents: diff.type === 'deleted' ? null : diff.newContent,
          userUpdatedContents: diff.type === 'deleted' ? null : diff.newContent,
          aiUpdatedContents: this.fileObjects[`root/${diff.path}`]?.draftContents || undefined,
          boundedContextId: '',
          id: '',
          prompt: '',
          tags: []
        }
      }

      this.diffObjects = Object.keys(finalDiffs)
        .sort()
        .reduce((acc, key) => {
          acc[key] = finalDiffs[key]
          return acc
        }, {})

      this.isLoading = false
    },
    async makeCommit (commitMessage) {
      if (!this.selectedRepo) {
        return
      }

      const updates = Object.entries(this.diffObjects).map(([path, file]) => {
        const oldContent = file.githubContents
        const newContent = file.originalContents

        let type
        if (oldContent === null && newContent !== null) {
          type = 'added'
        } else if (oldContent !== null && newContent === null) {
          type = 'deleted'
        } else if (oldContent !== newContent) {
          type = 'modified'
        } else {
          return null
        } // no change

        const update = { type, path: path.replace('root/', '') }

        if (type !== 'deleted') {
          update.content = newContent
        }

        return update
      }).filter(Boolean)

      if (updates.length) {
        const response = await gitHubApi.commitGitHubRepoFiles(
          this.githubAccountLogin,
          this.selectedRepo.owner.login,
          this.selectedRepo.name,
          'main',
          updates,
          commitMessage
        )

        if (response.success) {
          this.$store.commit('snackbar/showMessage', {
            content: updates.length + ' file(s) have been commited successfully',
            timeout: 3000,
            color: 'blue'
          }, { root: true })
        } else {
          this.$store.commit('snackbar/showMessage', {
            content: response.errorText,
            timeout: 3000,
            color: 'red'
          }, { root: true })
        }
      }

      this.commitMessage = ''
      this.diffObjects = {}
    },
    async unlinkGitHubRepo () {
      this.githubAccountLogin = ''
      this.githubRepo = ''
      this.githubBranch = null
      this.selectedRepo = null
      this.repos = []

      const updatedValues = {
        githubAccountLogin: null,
        githubRepo: null,
        githubBranch: null
      }
      await this.updateBoundedContext(updatedValues, 'unlinked GitHub repository from bounded context')
    },
    async pushSwaggerToGithub () {
      if (!this.selectedRepo || !this.githubAccountLogin) {
        this.$store.commit('snackbar/showMessage', {
          content: 'Connect a GitHub repository first.',
          timeout: 3000,
          color: 'red'
        }, { root: true })
        return
      }
      try {
        this.isLoading = true
        this.loadingText = 'Preparing Swagger file ...'
        const swagger = convertWorkflowToSwaggerYaml(this.currentWorkflow, this.boundedContext)
        const branchName = typeof this.githubBranch === 'string'
          ? this.githubBranch
          : (this.githubBranch?.name || 'main')
        const updates = [
          {
            type: 'modified',
            path: 'server/src/docs/swagger.yaml',
            content: swagger
          }
        ]
        this.loadingText = 'Committing Swagger to GitHub ...'
        const response = await gitHubApi.commitGitHubRepoFiles(
          this.githubAccountLogin,
          this.selectedRepo.owner.login,
          this.selectedRepo.name,
          branchName,
          updates,
          'chore: update API specification'
        )
        if (response.success) {
          this.$store.commit('snackbar/showMessage', {
            content: 'Swagger file pushed to GitHub.',
            timeout: 3000,
            color: 'green'
          }, { root: true })
          await this.checkSyncWithRepo()
        } else {
          this.$store.commit('snackbar/showMessage', {
            content: response.errorText,
            timeout: 3000,
            color: 'red'
          }, { root: true })
        }
      } finally {
        this.isLoading = false
      }
    },
    navigateToDomainView (issue) {
      this.$emit('navigateToDomainView', issue)
    }
  }
}
</script>

<style scoped>
.task-completed {
  opacity: 0.6;
}

.task-completed span {
  text-decoration: line-through;
}

[data-theme="dark"] .aggregates-sidebar-nav {
  background-color: var(--light-2);
}

[data-theme="dark"] .aggregates-sidebar-nav :deep(.v-btn.bg-grey-lighten-2) {
  background-color: var(--light-4) !important;
}
</style>
